import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import '@hotmart/cosmos/dist/styles/utilities/screenreaders.css'

import {
  togglePostModalEventDispatch,
  togglePostSidebarFullScreenEventDispatch
} from 'src/utils/customEvents'
import { MODAL_ACTIONS } from 'src/utils/customEvents/types'
import ModalHeader from 'src/components/Modal/header'
import ModalFooter from 'src/components/Modal/footer'
import useSendDataHubConsume from 'src/hook/useSendDatahubConsume/useSendDatahubConsume'

import clsx from 'clsx'
import { useGetLinkPreview } from 'src/hook/useGetLinkPreview'
import { PostType } from 'src/hook/usePosts'

import { LinkUtils } from 'src/utils/link'
import { NO_IMAGE } from 'src/assets/svg'
import { resetScroll } from 'src/utils/functions'
import { Actions, Entities, EventNames, ScreenNames } from 'src/utils/datahub/enums'
import {
  ArrivedFrom,
  OriginClick
} from 'src/components/courseCommunityAlert/courseCommunityAlert.enums'
import { useCreatePublication } from '../..'
import { PostModalActions } from '../postModalActions'
import { PostModalContent } from '../postModalContent'
import { PostModalFooter } from '../postModalFooter'
import { PostModalHeader } from '../postModalHeader'
import { getDefaultPublication } from './postModal.constants'
import { CustomModal, CustomModalBody } from './postModal.styles'
import { PostModalProps } from './postModal.types'

const PostModal = ({
  isOpen,
  title,
  description,
  postType,
  media,
  currentMedia,
  isOpenUpload,
  fileUploaded,
  isFileUploaded,
  descriptionOnlyText,
  linkUrl,
  mentions,
  setLinkUrl,
  onClose,
  setTitle,
  setDescription,
  setDescriptionOnlyText,
  setPostType,
  setMedia,
  setCurrentMedia,
  setFileUploaded,
  componentSource,
  setIsOpenUpload,
  setUploading,
  setMentions,
  onRemoveMedia,
  handleResetForm
}: PostModalProps) => {
  const { isLoadingCreatePublication, onCreatePublication } = useCreatePublication()
  const { data: linkMetadata, error: errorGettingLinkMeta } = useGetLinkPreview({ linkUrl })
  const { sendDataHubConsume } = useSendDataHubConsume()
  const [urlPostMeta, setUrlPostMeta] = useState({
    link: '',
    linkDomain: '',
    linkImageUrl: NO_IMAGE,
    linkTitle: ''
  })
  const [searchParams] = useSearchParams()

  const hasParamCameFromClass = searchParams.get('origin')
  const cameFromClass =
    hasParamCameFromClass === OriginClick.COMPLETED_COURSE ||
    hasParamCameFromClass === OriginClick.RATING

  useEffect(() => {
    const listener = () => {
      togglePostModalEventDispatch({ action: MODAL_ACTIONS.CLOSE })
      setIsOpenUpload(false)
    }

    const hotModal = document.querySelector('hot-modal')

    if (isOpen && hotModal) {
      return hotModal.addEventListener('close', listener)
    }

    return () => hotModal?.removeEventListener('close', listener)
  }, [isOpen, togglePostModalEventDispatch])

  useEffect(() => {
    if (!linkMetadata || errorGettingLinkMeta) return

    const extractedLink =
      LinkUtils.validateLink(LinkUtils.extractURLfromText(description ?? linkMetadata.url) ?? '') ??
      ''

    const link = LinkUtils.isValidHttpURL(extractedLink) ? new URL(extractedLink) : null

    setUrlPostMeta((prev) => ({
      ...prev,
      link: link?.href ?? prev.link,
      linkDomain: linkMetadata?.url ?? prev.linkDomain,
      linkImageUrl: linkMetadata?.image ?? prev.linkImageUrl,
      linkTitle: linkMetadata?.title ?? prev.linkTitle
    }))

    setPostType(PostType.URL)
  }, [linkMetadata, errorGettingLinkMeta])

  const handleModalFullScreen = () => {
    togglePostModalEventDispatch({ action: MODAL_ACTIONS.CLOSE, showFullScreen: true })
    togglePostSidebarFullScreenEventDispatch({ action: MODAL_ACTIONS.OPEN })
  }

  const handleCloseModal = () => {
    setIsOpenUpload(false)
    onClose()
  }

  const handleSubmit = async () => {
    let publication = {
      ...getDefaultPublication(),
      ...media,
      title,
      description,
      postType
    }

    if (cameFromClass) {
      sendDataHubConsume({
        action: Actions.VIEW,
        entity: Entities.COMMUNITY,
        event: {
          screenName: ScreenNames.COMMUNITY_POST_DETAIL,
          eventName: EventNames.CLICK_TO_CREATE_POST,
          arrivedFrom: ArrivedFrom.CLASSROOM
        }
      })
    }

    const isValidPostMeta = Object.values(urlPostMeta).every((value) => value !== '')

    if (publication.postType === PostType.URL && isValidPostMeta) {
      publication = { ...publication, ...urlPostMeta }
    } else if (publication.postType === PostType.URL && !isValidPostMeta) {
      publication.postType = PostType.TEXT
    }

    try {
      await onCreatePublication({
        publication,
        descriptionOnlyText,
        handleResetForm,
        componentSource
      })

      resetScroll()
    } catch {
      throw new Error('Error when publishing')
    }
  }

  const classNamePostModalActions = clsx(
    '_p-4 _p-md-5 _d-flex _flex-wrap _justify-content-start _align-items-center _w-full _border-bottom _border-gray-200',
    {
      '_pt-4 _pb-5': isOpenUpload
    }
  )

  return (
    <CustomModal show={isOpen} onClose={handleCloseModal}>
      <ModalHeader className="_px-5 _py-4 _border-bottom _border-gray-200	">
        <PostModalHeader
          handleModalFullScreen={handleModalFullScreen}
          handleOnClose={handleCloseModal}
        />
      </ModalHeader>

      <CustomModalBody
        id="post-modal"
        className="_p-0 _pt-6 _px-8 _border-bottom _border-gray-200 _overflow-x-hidden _overflow-y-hidden"
      >
        <PostModalContent
          title={title}
          linkUrl={linkUrl}
          postType={postType}
          mentions={mentions}
          setPostType={setPostType}
          setTitle={setTitle}
          setLinkUrl={setLinkUrl}
          description={description}
          isOpenUpload={isOpenUpload}
          isFileUploaded={isFileUploaded}
          setDescription={setDescription}
          setDescriptionOnlyText={setDescriptionOnlyText}
          setMentions={setMentions}
        />
      </CustomModalBody>

      <PostModalActions
        className={classNamePostModalActions}
        isOpenUpload={isOpenUpload}
        fileUploaded={fileUploaded}
        isFileUploaded={isFileUploaded}
        currentMedia={currentMedia}
        postType={postType}
        setPostType={setPostType}
        setMedia={setMedia}
        setCurrentMedia={setCurrentMedia}
        setIsOpenUpload={setIsOpenUpload}
        setFileUploaded={setFileUploaded}
        onRemoveMedia={onRemoveMedia}
        setUploading={setUploading}
      />

      <ModalFooter className="_p-5 _bg-gray-100">
        <PostModalFooter
          title={title}
          className="_d-flex _ml-auto"
          onSubmit={handleSubmit}
          isLoadingSubmit={isLoadingCreatePublication}
        />
      </ModalFooter>
    </CustomModal>
  )
}

export default PostModal
